import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Stepper,
  Step,
  StepLabel,
  MenuItem,
  Card,
  CardContent,
  Checkbox,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Send as SendIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignAPI, locationAPI, screenAPI, dashboardAPI } from '../services/api';
import CampaignFormStep1 from '../components/CampaignFormStep1';
import SlotSelector from '../components/SlotSelector';
import CampaignReview from '../components/CampaignReview';

const steps = ['Campaign Details', 'Assign Slots', 'Upload Creative', 'Review & Submit'];

const CreateCampaign = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);

  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [screens, setScreens] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [campaignId, setCampaignId] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    clientName: '',
    offerNumber: '',
    startDate: '',
    endDate: '',
    selectedLocation: '',
    selectedScreens: [],
  });

  useEffect(() => {
    fetchLocations();
    if (isEditMode) {
      fetchCampaignData();
    }
  }, [id]);

  useEffect(() => {
    if (formData.selectedLocation) {
      fetchScreensByLocation();
    }
  }, [formData.selectedLocation]);

  const fetchLocations = async () => {
    try {
      const response = await locationAPI.getLocationsDropdown();
      console.log('Locations response:', response.data);
      // Handle different response structures
      const locationsData = response.data?.data || response.data?.locations || response.data || [];
      const locationsArray = Array.isArray(locationsData) ? locationsData : [];
      console.log('Parsed locations:', locationsArray);

      try {
        const userScreensResponse = await dashboardAPI.getMyScreens();
        const userScreens = userScreensResponse.data?.data?.screens || userScreensResponse.data?.screens || [];
        const userLocationIds = new Set(userScreens.map(s => s.locationId?._id || s.locationId).filter(Boolean));

        const userLocations = locationsArray.filter(loc => userLocationIds.has(loc._id));
        setLocations(userLocations.length > 0 ? userLocations : locationsArray);

        if (userLocations.length === 0 && locationsArray.length > 0) {
          toast.warning('You don\'t have screens at any location. Please contact admin to assign screens to you.');
        }
      } catch {
        setLocations(locationsArray);
      }

      if (locationsArray.length === 0) {
        toast.info('No locations available. Please contact admin to add locations.');
      }
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch locations');
      setLocations([]); // Ensure it's always an array
    }
  };

  const fetchCampaignData = async () => {
    try {
      setLoading(true);
      const response = await campaignAPI.getCampaignById(id);
      const campaign = response.data?.data?.campaign || response.data?.data || response.data;

      if (!campaign) {
        toast.error('Campaign data not found');
        navigate('/campaigns');
        return;
      }

      // Extract location ID from assignedSlots (handle both populated and non-populated)
      const locationId = campaign.assignedSlots?.[0]?.locationId?._id || campaign.assignedSlots?.[0]?.locationId || '';

      // Pre-select screens if slots are already assigned
      const screenIds = campaign.assignedSlots ? [...new Set(campaign.assignedSlots.map(slot => {
        return slot.screenId?._id || slot.screenId;
      }).filter(Boolean))] : [];

      // Pre-select slots for display
      if (campaign.assignedSlots && campaign.assignedSlots.length > 0) {
        setSelectedSlots(campaign.assignedSlots.map(slot => ({
          screenId: slot.screenId?._id || slot.screenId,
          slotNumber: slot.slotNumber,
          siteCode: slot.siteCode,
          locationId: slot.locationId?._id || slot.locationId,
        })));
      }

      setFormData({
        name: campaign.campaignName,
        clientName: campaign.clientName,
        offerNumber: campaign.offerNumber || '',
        startDate: campaign.startDate?.split('T')[0],
        endDate: campaign.endDate?.split('T')[0],
        selectedLocation: locationId,
        selectedScreens: screenIds,
      });

      setCampaignId(campaign._id);

      if (campaign.assignedSlots && campaign.assignedSlots.length > 0) {
        setActiveStep(2); // Skip to upload creative if slots are assigned
      } else if (campaign.fileUrl || (campaign.mediaFiles && campaign.mediaFiles.length > 0)) {
        setActiveStep(3); // Skip to review if creative is uploaded
      }
    } catch (error) {
      console.error('Error fetching campaign:', error);
      toast.error('Failed to fetch campaign data');
      navigate('/campaigns');
    } finally {
      setLoading(false);
    }
  };

  const fetchScreensByLocation = async () => {
    try {
      const response = await screenAPI.getScreensByLocation(formData.selectedLocation);
      console.log('Screens response:', response.data);
      // Handle different response structures
      const screensData = response.data?.data || response.data?.screens || response.data || [];
      const screensArray = Array.isArray(screensData) ? screensData : [];
      console.log('Parsed screens:', screensArray);
      setScreens(screensArray);

      if (screensArray.length === 0) {
        toast.info('No screens available at this location.');
      } else {
        // Auto-select all screens and check availability
        const screenIds = screensArray.map(screen => screen._id);
        setFormData(prev => ({
          ...prev,
          selectedScreens: screenIds,
        }));
      }
    } catch (error) {
      console.error('Error fetching screens:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch screens');
      setScreens([]); // Ensure it's always an array
    }
  };

  const autoCheckAvailability = async (locationId, screenIds) => {
    try {
      const response = await campaignAPI.checkSlotAvailability({
        locationId: locationId,
        startDate: formData.startDate,
        endDate: formData.endDate,
        excludeCampaignId: campaignId, // FIXED: Exclude current campaign when editing
      });
      // Handle different response structures
      const slotsData = response.data?.data?.availability || response.data?.data || response.data?.slots || response.data || [];
      const allSlots = Array.isArray(slotsData) ? slotsData : [];
      const availableCount = allSlots.filter(slot => slot.available).length;
      setAvailableSlots(allSlots);

      if (allSlots.length > 0) {
        toast.success(`Found ${allSlots.length} total slots (${availableCount} available)`);
      } else {
        toast.warning('No slots found for the selected dates');
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      // Don't show error toast here as it's automatic
      setAvailableSlots([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleNext = async () => {
    if (activeStep === 0) {
      // Step 1: Create/Update Campaign Details
      if (!validateStep1()) return;
      const success = await saveCampaignDetails();
      if (!success) return;
    } else if (activeStep === 1) {
      // Step 2: Assign Slots
      if (selectedSlots.length === 0) {
        toast.error('Please select at least one slot');
        return;
      }
      const success = await assignSlots();
      if (!success) return;
    } else if (activeStep === 2) {
      // Step 3: Upload Creative
      if (uploadedFiles.length === 0 && !isEditMode) {
        toast.error('Please upload at least one creative file');
        return;
      }
      if (uploadedFiles.length > 0) {
        const success = await uploadCreatives();
        if (!success) return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const validateStep1 = () => {
    if (!formData.name || !formData.clientName || !formData.offerNumber) {
      toast.error('Please fill in all required fields');
      return false;
    }
    return true;
  };

  const saveCampaignDetails = async () => {
    try {
      setLoading(true);
      const data = {
        campaignName: formData.name,
        clientName: formData.clientName,
        offerNumber: formData.offerNumber || '',
        contentType: 'image',
        duration: 15,
      };

      // Only include dates if they're set
      if (formData.startDate) {
        data.startDate = formData.startDate;
      }
      if (formData.endDate) {
        data.endDate = formData.endDate;
      }

      let response;
      if (campaignId) {
        response = await campaignAPI.updateCampaign(campaignId, data);
      } else {
        response = await campaignAPI.createCampaign(data);
        const createdCampaign = response.data?.data?.campaign || response.data?.data || response.data;
        setCampaignId(createdCampaign._id || createdCampaign.id);
      }
      toast.success('Campaign details saved');
      return true;
    } catch (error) {
      console.error('Error saving campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to save campaign');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const uploadCreatives = async () => {
    try {
      setLoading(true);
      const formDataUpload = new FormData();

      // Append all files
      uploadedFiles.forEach((file) => {
        formDataUpload.append('creatives', file);
      });

      await campaignAPI.uploadMultipleCreatives(campaignId, formDataUpload);
      toast.success(`${uploadedFiles.length} creative(s) uploaded successfully`);
      return true;
    } catch (error) {
      console.error('Error uploading creatives:', error);
      toast.error(error.response?.data?.message || 'Failed to upload creatives');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const checkAvailability = async () => {
    if (!formData.selectedLocation) {
      toast.error('Please select a location');
      return;
    }

    try {
      setLoading(true);
      const response = await campaignAPI.checkSlotAvailability({
        locationId: formData.selectedLocation,
        startDate: formData.startDate,
        endDate: formData.endDate,
        excludeCampaignId: campaignId, // FIXED: Exclude current campaign when editing
      });
      // Handle different response structures
      const slotsData = response.data?.data?.availability || response.data?.data || response.data?.slots || response.data || [];
      const allSlots = Array.isArray(slotsData) ? slotsData : [];
      const availableCount = allSlots.filter(slot => slot.available).length;
      setAvailableSlots(allSlots);

      if (allSlots.length > 0) {
        toast.success(`Found ${allSlots.length} total slots (${availableCount} available)`);
      } else {
        toast.warning('No slots found for the selected dates');
      }
    } catch (error) {
      console.error('Error checking availability:', error);
      toast.error(error.response?.data?.message || 'Failed to check availability');
      setAvailableSlots([]); // Ensure it's always an array
    } finally {
      setLoading(false);
    }
  };

  const assignSlots = async () => {
    try {
      setLoading(true);
      
      await campaignAPI.assignSlots(campaignId, {
        slots: selectedSlots,
      });
      toast.success('Slots assigned successfully');
      return true;
    } catch (error) {
      console.error('Error assigning slots:', error);
      toast.error(error.response?.data?.message || 'Failed to assign slots');
      return false;
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitForApproval = async () => {
    try {
      setLoading(true);
      await campaignAPI.submitForApproval(campaignId);
      toast.success('Campaign submitted for approval');
      navigate('/campaigns');
    } catch (error) {
      console.error('Error submitting campaign:', error);
      toast.error(error.response?.data?.message || 'Failed to submit campaign');
    } finally {
      setLoading(false);
    }
  };

  const handleSlotToggle = (slot) => {
    const slotKey = `${slot.screenId}-${slot.slotNumber}`;
    const exists = selectedSlots.find(
      (s) => s.screenId === slot.screenId && s.slotNumber === slot.slotNumber
    );

    if (exists) {
      setSelectedSlots(selectedSlots.filter(
        (s) => !(s.screenId === slot.screenId && s.slotNumber === slot.slotNumber)
      ));
    } else {
      setSelectedSlots([...selectedSlots, {
        siteCode: slot.siteCode,
        slotNumber: slot.slotNumber,
        screenId: slot.screenId,
        locationId: formData.selectedLocation,
      }]);
    }
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <CampaignFormStep1
              formData={formData}
              handleInputChange={handleInputChange}
              errors={{}}
            />
          </Box>
        );

      case 1:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Select a location and screens to check available time slots for your campaign.
            </Alert>

            <Grid container spacing={3} mb={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  select
                  label="Select Location"
                  name="selectedLocation"
                  value={formData.selectedLocation}
                  onChange={handleInputChange}
                  helperText={locations.length === 0 ? 'No locations available' : 'Choose a location to see available screens'}
                  SelectProps={{
                    displayEmpty: true,
                  }}
                >
                  {locations.length === 0 ? (
                    <MenuItem value="" disabled>
                      No locations available
                    </MenuItem>
                  ) : (
                    locations.map((location) => (
                      <MenuItem key={location._id} value={location._id}>
                        {location.name || location.locationName} {location.city ? `- ${location.city}` : ''}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>
              <Grid item xs={12} md={6}>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={checkAvailability}
                  disabled={!formData.selectedLocation || loading}
                  sx={{ height: '56px' }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Refresh Slot Availability'}
                </Button>
              </Grid>
            </Grid>

            {formData.selectedLocation && screens.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Select Screens:
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Choose one or more screens where you want to display your campaign
                </Typography>
                <Grid container spacing={2}>
                  {screens.map((screen) => (
                    <Grid item xs={12} md={6} key={screen._id}>
                      <Card
                        variant="outlined"
                        sx={{
                          cursor: 'pointer',
                          border: formData.selectedScreens.includes(screen._id) ? '2px solid' : '1px solid',
                          borderColor: formData.selectedScreens.includes(screen._id) ? 'primary.main' : 'divider',
                          '&:hover': { borderColor: 'primary.main' },
                        }}
                        onClick={() => {
                          if (formData.selectedScreens.includes(screen._id)) {
                            setFormData({
                              ...formData,
                              selectedScreens: formData.selectedScreens.filter(
                                (id) => id !== screen._id
                              ),
                            });
                          } else {
                            setFormData({
                              ...formData,
                              selectedScreens: [...formData.selectedScreens, screen._id],
                            });
                          }
                        }}
                      >
                        <CardContent>
                          <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box>
                              <Typography variant="body1" fontWeight="bold">
                                {screen.name || screen.screenName}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ID: {screen.screenId} | Site: {screen.siteCode}
                              </Typography>
                              {screen.resolution && (
                                <Typography variant="caption" color="text.secondary">
                                  {screen.resolution} • {screen.orientation}
                                </Typography>
                              )}
                            </Box>
                            <Checkbox
                              checked={formData.selectedScreens.includes(screen._id)}
                              onChange={() => { }} // Handled by card click
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

            {formData.selectedLocation && screens.length === 0 && (
              <Alert severity="warning" sx={{ mb: 3 }}>
                No screens available at this location. Please select a different location or contact admin.
              </Alert>
            )}

            {availableSlots.length > 0 && (
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>
                  Available Slots:
                </Typography>
                <SlotSelector
                  availableSlots={availableSlots}
                  selectedSlots={selectedSlots}
                  handleSlotToggle={handleSlotToggle}
                />
              </Box>
            )}
          </Box>
        );

      case 2:
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              Upload your campaign creatives (images or videos). You can upload multiple files. Max file size: 100MB per file.
            </Alert>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main' },
              }}
              onClick={() => document.getElementById('creative-upload').click()}
            >
              <input
                id="creative-upload"
                type="file"
                accept="image/*,video/*"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const files = Array.from(e.target.files);
                  setUploadedFiles(files);
                }}
              />
              <UploadIcon sx={{ fontSize: 64, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6">
                {uploadedFiles.length > 0
                  ? `${uploadedFiles.length} file(s) selected`
                  : 'Click to upload creatives'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Supported formats: JPG, PNG, MP4, MOV (Multiple files allowed)
              </Typography>
            </Box>

            {uploadedFiles.length > 0 && (
              <Box mt={3}>
                <Typography variant="h6" gutterBottom>
                  Selected Files:
                </Typography>
                <List>
                  {uploadedFiles.map((file, index) => (
                    <ListItem key={index}>
                      <ListItemText
                        primary={file.name}
                        secondary={`${(file.size / 1024 / 1024).toFixed(2)} MB`}
                      />
                      <Button
                        color="error"
                        onClick={() => {
                          setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
                        }}
                      >
                        Remove
                      </Button>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}
          </Box>
        );

      case 3:
        return (
          <Box>
            <CampaignReview
              formData={formData}
              uploadedFiles={uploadedFiles}
              selectedSlots={selectedSlots}
            />
            
            <Box mt={4} textAlign="center">
              <Button
                variant="contained"
                size="large"
                startIcon={<SendIcon />}
                onClick={handleSubmitForApproval}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Submit for Approval'}
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" fontWeight="bold" mb={3}>
        {isEditMode ? 'Edit Campaign' : 'Create New Campaign'}
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {renderStepContent()}

        {activeStep < 3 && (
          <Box display="flex" justifyContent="space-between" mt={4}>
            <Button disabled={activeStep === 0} onClick={handleBack}>
              Back
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : activeStep === 2 ? 'Continue' : 'Next'}
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export default CreateCampaign;